<template>
  <el-dialog
    :visible.sync="dialogVisible"
    title="Select Analysis Tier"
    width="900px"
    :before-close="handleClose"
    class="tier-modal"
  >
    <div class="tier-selection">
      <p class="modal-description">
        Choose the analysis tier that best fits your needs. Each tier includes all features from previous tiers.
      </p>

      <div class="tiers-container">
        <!-- Tier 1 -->
        <div
          :class="['tier-card', { selected: selectedTier === 1 }]"
          @click="selectTier(1)"
        >
          <div class="tier-header tier-1">
            <div class="tier-icon">
              <i class="el-icon-box"></i>
            </div>
            <h3>Tier 1</h3>
            <p class="tier-name">Core Metadata Pack</p>
          </div>
          <div class="tier-body">
            <p class="tier-description">
              High-level tracking, video discovery, and monitoring basic stats.
            </p>
            <div class="tier-features">
              <div class="feature-group">
                <h4><i class="el-icon-video-camera"></i> Video Metadata & Engagement</h4>
                <ul>
                  <li>Video ID, Title, Description, Tags</li>
                  <li>Category, Length, Published Date</li>
                  <li>View Count, Like Count, Comments</li>
                </ul>
              </div>
              <div class="feature-group">
                <h4><i class="el-icon-user"></i> Channel Analysis</h4>
                <ul>
                  <li>Channel Info & Verification</li>
                  <li>Subscriber Count, Upload Frequency</li>
                </ul>
              </div>
              <div class="feature-group">
                <h4><i class="el-icon-document-checked"></i> Platform Claim Status</h4>
                <ul>
                  <li>Music match verification</li>
                  <li>Third-party claims detection</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="tier-footer">
            <el-tag v-if="selectedTier === 1" type="success" size="medium">
              <i class="el-icon-check"></i> Selected
            </el-tag>
          </div>
        </div>

        <!-- Tier 2 -->
        <div
          :class="['tier-card', { selected: selectedTier === 2 }]"
          @click="selectTier(2)"
        >
          <div class="tier-header tier-2">
            <div class="tier-icon">
              <i class="el-icon-headset"></i>
            </div>
            <h3>Tier 2</h3>
            <p class="tier-name">Advanced Audio Report</p>
          </div>
          <div class="tier-body">
            <p class="tier-description">
              Deep contextual analysis of audio content for music rights holders.
            </p>
            <div class="tier-features">
              <div class="feature-group">
                <h4><i class="el-icon-success"></i> All Tier 1 Features</h4>
              </div>
              <div class="feature-group">
                <h4><i class="el-icon-headset"></i> Full Audio Analysis: Music</h4>
                <ul>
                  <li>Continuous vs. multiple clips detection</li>
                  <li>Usage timestamps in video & reference</li>
                  <li>Music vs. Vocal Loudness Ratio</li>
                  <li>Detection of other music (ACRCloud/PEX)</li>
                </ul>
              </div>
              <div class="feature-group">
                <h4><i class="el-icon-microphone"></i> Vocal & Spoken Content</h4>
                <ul>
                  <li>Full audio transcription</li>
                  <li>Language & AI voiceover detection</li>
                  <li>Sentiment & topic analysis</li>
                  <li>Offensive content detection</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="tier-footer">
            <el-tag v-if="selectedTier === 2" type="success" size="medium">
              <i class="el-icon-check"></i> Selected
            </el-tag>
          </div>
        </div>

        <!-- Tier 3 -->
        <div
          :class="['tier-card', { selected: selectedTier === 3 }]"
          @click="selectTier(3)"
        >
          <div class="tier-header tier-3">
            <div class="tier-icon">
              <i class="el-icon-view"></i>
            </div>
            <h3>Tier 3</h3>
            <p class="tier-name">Complete 360 Report</p>
          </div>
          <div class="tier-body">
            <p class="tier-description">
              Total brand safety assurance and complete contextual understanding.
            </p>
            <div class="tier-features">
              <div class="feature-group">
                <h4><i class="el-icon-success"></i> All Tier 1 & Tier 2 Features</h4>
              </div>
              <div class="feature-group">
                <h4><i class="el-icon-picture"></i> Full Visual Analysis</h4>
                <ul>
                  <li>Still image/AI-generated detection</li>
                  <li>Visual scene classification</li>
                  <li>Offensive/violent content detection</li>
                  <li>Brand/logo/copyrighted image detection</li>
                </ul>
              </div>
              <div class="feature-group">
                <h4><i class="el-icon-document"></i> On-Screen Text (OCR)</h4>
                <ul>
                  <li>Full OCR text extraction</li>
                  <li>Text sentiment & topic analysis</li>
                  <li>Offensive text detection</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="tier-footer">
            <el-tag v-if="selectedTier === 3" type="success" size="medium">
              <i class="el-icon-check"></i> Selected
            </el-tag>
          </div>
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Cancel</el-button>
      <el-button
        type="primary"
        :disabled="selectedTier === null"
        @click="handleConfirm"
      >
        Confirm Selection
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'TierSelectionModal',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    currentTier: {
      type: Number,
      default: null
    }
  },

  data: function() {
    return {
      selectedTier: this.currentTier
    }
  },

  computed: {
    dialogVisible: {
      get: function() {
        return this.visible
      },
      set: function(value) {
        if (!value) {
          this.$emit('update:visible', false)
        }
      }
    }
  },

  watch: {
    currentTier: function(newVal) {
      this.selectedTier = newVal
    },
    visible: function(newVal) {
      if (newVal) {
        this.selectedTier = this.currentTier
      }
    }
  },

  methods: {
    selectTier: function(tier) {
      this.selectedTier = tier
    },

    handleConfirm: function() {
      if (this.selectedTier !== null) {
        this.$emit('tier-selected', this.selectedTier)
        this.$emit('update:visible', false)
      }
    },

    handleClose: function() {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style lang="scss" scoped>
.tier-modal {
  ::v-deep .el-dialog {
    border-radius: 12px;
  }

  ::v-deep .el-dialog__header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px;
    border-radius: 12px 12px 0 0;

    .el-dialog__title {
      color: white;
      font-size: 20px;
      font-weight: 600;
    }

    .el-dialog__headerbtn .el-dialog__close {
      color: white;
      font-size: 20px;

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }

  ::v-deep .el-dialog__body {
    padding: 30px 20px;
  }

  ::v-deep .el-dialog__footer {
    padding: 15px 20px;
    border-top: 1px solid #e9ecef;
  }
}

.tier-selection {
  .modal-description {
    text-align: center;
    color: #606266;
    font-size: 14px;
    margin: 0 0 25px 0;
    line-height: 1.6;
  }

  .tiers-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
    }
  }

  .tier-card {
    border: 2px solid #e9ecef;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    background: white;
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    }

    &.selected {
      border-color: #667eea;
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.25);
    }

    .tier-header {
      padding: 20px;
      color: white;
      text-align: center;

      &.tier-1 {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      }

      &.tier-2 {
        background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
      }

      &.tier-3 {
        background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      }

      .tier-icon {
        font-size: 36px;
        margin-bottom: 10px;

        i {
          font-weight: bold;
        }
      }

      h3 {
        margin: 0 0 5px 0;
        font-size: 24px;
        font-weight: 700;
      }

      .tier-name {
        margin: 0;
        font-size: 14px;
        opacity: 0.95;
        font-weight: 500;
      }
    }

    .tier-body {
      padding: 20px;
      flex: 1;
      display: flex;
      flex-direction: column;

      .tier-description {
        font-size: 13px;
        color: #606266;
        margin: 0 0 15px 0;
        line-height: 1.5;
        font-style: italic;
      }

      .tier-features {
        .feature-group {
          margin-bottom: 15px;

          &:last-child {
            margin-bottom: 0;
          }

          h4 {
            font-size: 13px;
            color: #495057;
            margin: 0 0 8px 0;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 6px;

            i {
              color: #667eea;
              font-size: 14px;
            }
          }

          ul {
            margin: 0;
            padding-left: 20px;
            list-style: none;

            li {
              font-size: 12px;
              color: #606266;
              margin-bottom: 4px;
              position: relative;
              line-height: 1.4;

              &:before {
                content: '•';
                position: absolute;
                left: -12px;
                color: #667eea;
              }

              &:last-child {
                margin-bottom: 0;
              }
            }
          }
        }
      }
    }

    .tier-footer {
      padding: 15px 20px;
      border-top: 1px solid #e9ecef;
      text-align: center;
      min-height: 56px;
      display: flex;
      align-items: center;
      justify-content: center;

      .el-tag {
        font-weight: 600;

        i {
          margin-right: 5px;
        }
      }
    }
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
